import * as React from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FenceSegment } from "models/fenceSegment";
import { Button, Card, CardBody, Spinner } from "reactstrap";
import { EstimateApi } from "api/api";
import { GetContactInformationModal } from "../GetContactInformationModal";
import { ContactInformation } from "models/contact";

type EstimateTotalProps = {
  fences: FenceSegment[];
  isContactInformationProvided: boolean;
  onContactInformationProvided: (contact: ContactInformation) => void;
  onGetFullQuoteClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
};

const formatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

export const EstimateTotal = function (props: EstimateTotalProps) {
  const [priceRange, setPriceRange] = React.useState<{ min: number; max: number }>();
  const [openContactInfoModal, toggleContactInfoModal] = React.useState(false);
  const [isLoading, setLoading] = React.useState(false);
  const { estimateId } = useParams();

  React.useEffect(() => {
    if (props.fences.length === 0 || props.fences.every((f) => f.isIncomplete())) return;
    setLoading(true);
    EstimateApi.calculateEstimatePrice(estimateId!, props.fences)
      .then((data) => {
        setPriceRange({ max: data.max, min: data.min });
        setLoading(false);
      })
      .catch(() => toast.error("Error calculating prices"));
  }, [setPriceRange, props.fences, estimateId]);

  const handleShowPriceBtnClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (props.fences.some((f) => f.isIncomplete())) {
      toast.error("Please add missing style or delete the incomplete fence segment");
    } else {
      toggleContactInfoModal(true);
    }
  };

  const handleGetFullQuoteBtnClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (props.fences.some((f) => f.isIncomplete())) {
      toast.error("Please add missing style or delete the incomplete fence segment");
    } else {
      props.onGetFullQuoteClick(e);
    }
  };

  if (props.fences.every((f) => f.isIncomplete())) return null;

  return (
    <>
      {openContactInfoModal && (
        <GetContactInformationModal
          onClose={() => toggleContactInfoModal(false)}
          onSubmit={(contact) => {
            props.onContactInformationProvided(contact);
            toggleContactInfoModal(false);
          }}
        />
      )}
      <Card className="bg-primary text-white rounded-3 shadow my-2">
        <CardBody className="text-center">
          <div className="ds-label text-white-50 mb-1">Estimate Range</div>
          <div className="fs-4 fw-800 text-white mb-3">
            {isLoading ? (
              <Spinner color="light" size="sm" />
            ) : (
              <PriceDisplay
                isObfuscated={!props.isContactInformationProvided}
                min={priceRange?.min || 0}
                max={priceRange?.max || 0}
              />
            )}
          </div>
          <div className="d-grid">
            {!props.isContactInformationProvided ? (
              <Button color="success" size="lg" onClick={handleShowPriceBtnClick}>
                Show Price
              </Button>
            ) : (
              <Button color="success" size="lg" onClick={handleGetFullQuoteBtnClick}>
                Get Full Quote →
              </Button>
            )}
          </div>
        </CardBody>
      </Card>
    </>
  );
};

type PriceDisplayProps = { isObfuscated: boolean; min: number; max: number };
function PriceDisplay(props: PriceDisplayProps) {
  let min = formatter.format(props.min);
  let max = formatter.format(props.max);
  if (props.isObfuscated) {
    min = min.replace(/[0-9]/g, "X");
    max = max.replace(/[0-9]/g, "X");
  }
  return (
    <span className={props.isObfuscated ? "text-warning" : "text-white"}>
      {min} – {max}
    </span>
  );
}
